import { useState } from 'react';
import type { CountryCode } from 'libphonenumber-js';

import { countryFromDialCode, resolveDialCode } from '../utils/phone-helpers';

/**
 * Owns the selected country as local state, re-synced from `dialCode` only when it no longer
 * matches the currently selected country's own dial code — the "adjusting state during render"
 * pattern for a controlled prop that only partially determines local state. This lets selecting a
 * country that shares a calling code (e.g. Canada under +1) stick instead of snapping back to that
 * code's "main" country (e.g. the US) on the next render.
 */
export function useSelectedCountry(dialCode: string | undefined): [CountryCode, (code: CountryCode) => void] {
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(() => countryFromDialCode(dialCode ?? ''));
  const [lastSyncedDialCode, setLastSyncedDialCode] = useState(dialCode);

  if (dialCode !== lastSyncedDialCode) {
    setLastSyncedDialCode(dialCode);
    if (dialCode && resolveDialCode(selectedCountry) !== dialCode) {
      setSelectedCountry(countryFromDialCode(dialCode));
    }
  }

  return [selectedCountry, setSelectedCountry];
}
